/** The glyphs the tray needs: one per panel, plus the two the layer stack grew. */

const BOX = { viewBox: "0 0 16 16", width: 14, height: 14, "aria-hidden": true } as const;

const STROKE = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.4,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

/** The space: the frame everything else is mounted onto. */
export function SpaceIcon() {
  return (
    <svg {...BOX}>
      <rect {...STROKE} x="1.8" y="2.6" width="12.4" height="10.8" rx="1.6" />
      <path {...STROKE} d="M1.8 6h12.4" />
    </svg>
  );
}

/** One added activity. */
export function ActivityIcon() {
  return (
    <svg {...BOX}>
      <rect {...STROKE} x="2.4" y="2.4" width="11.2" height="11.2" rx="2" />
      <path {...STROKE} d="M6 6h4v4H6z" />
    </svg>
  );
}

/** The layer stack of one canvas. */
export function LayersIcon() {
  return (
    <svg {...BOX}>
      <path {...STROKE} d="M8 1.8 14.2 5 8 8.2 1.8 5 8 1.8Z" />
      <path {...STROKE} d="M1.8 8.4 8 11.6l6.2-3.2" />
      <path {...STROKE} d="M1.8 11.6 8 14.8l6.2-3.2" />
    </svg>
  );
}

/** A configured source-to-target connection. */
export function BindingsIcon() {
  return (
    <svg {...BOX}>
      <circle {...STROKE} cx="3.6" cy="4.2" r="1.8" />
      <circle {...STROKE} cx="12.4" cy="11.8" r="1.8" />
      <path {...STROKE} d="M5.2 5.4c2.6 0 2.6 5.2 5.6 5.2" />
    </svg>
  );
}

/** Everything this space could add. */
export function CatalogIcon() {
  return (
    <svg {...BOX}>
      <path {...STROKE} d="M2.4 3.4h4.2a1.4 1.4 0 0 1 1.4 1.4v8.2a1.4 1.4 0 0 0-1.4-1.4H2.4Z" />
      <path {...STROKE} d="M13.6 3.4H9.4A1.4 1.4 0 0 0 8 4.8v8.2a1.4 1.4 0 0 1 1.4-1.4h4.2Z" />
    </svg>
  );
}

/** Who reaches what: a person, and the key that admits them. */
export function AccessIcon() {
  return (
    <svg {...BOX}>
      <circle {...STROKE} cx="6" cy="5.4" r="2.4" />
      <path {...STROKE} d="M1.8 13.4c0-2.4 1.9-3.8 4.2-3.8s4.2 1.4 4.2 3.8" />
      <path {...STROKE} d="M11.4 6.2h2.8M13 4.6v3.2" />
    </svg>
  );
}

/** Every canvas the space declares: framed surfaces, one behind the other. */
export function CanvasesIcon() {
  return (
    <svg {...BOX}>
      <rect {...STROKE} x="1.8" y="3.4" width="8.6" height="6.6" rx="1.2" />
      <path {...STROKE} d="M5.6 12.6h8.6V6" />
    </svg>
  );
}

/** What is still missing: an outline with a gap in it. */
export function NeedsIcon() {
  return (
    <svg {...BOX}>
      <path {...STROKE} d="M8 1.8 14.2 5.2v5.6L8 14.2 1.8 10.8V5.2Z" />
      <path {...STROKE} d="M8 5v3.6M8 10.8v.1" />
    </svg>
  );
}

/** A bundle: a root with members hanging off it. */
export function CompositionIcon() {
  return (
    <svg {...BOX}>
      <rect {...STROKE} x="5.6" y="1.8" width="4.8" height="3.4" rx="1" />
      <rect {...STROKE} x="1.8" y="10.8" width="4" height="3.4" rx="1" />
      <rect {...STROKE} x="10.2" y="10.8" width="4" height="3.4" rx="1" />
      <path {...STROKE} d="M8 5.2v3.2M3.8 10.8V8.4h8.4v2.4" />
    </svg>
  );
}

/** What the running mounts hold: rows in a store. */
export function StateIcon() {
  return (
    <svg {...BOX}>
      <ellipse {...STROKE} cx="8" cy="4" rx="5.4" ry="2.2" />
      <path {...STROKE} d="M2.6 4v8c0 1.2 2.4 2.2 5.4 2.2s5.4-1 5.4-2.2V4" />
      <path {...STROKE} d="M2.6 8c0 1.2 2.4 2.2 5.4 2.2s5.4-1 5.4-2.2" />
    </svg>
  );
}

/** The ability system: a spark held inside a bound, which is an effect on an attribute. */
export function AbilityIcon() {
  return (
    <svg {...BOX}>
      <circle {...STROKE} cx="8" cy="8" r="5.6" />
      <path {...STROKE} d="M8.6 4.6 6.2 8.4h3.6L7.4 11.6" />
    </svg>
  );
}

/** A cue: one instant reaching outward. */
export function CuesIcon() {
  return (
    <svg {...BOX}>
      <circle {...STROKE} cx="8" cy="8" r="2" />
      <path {...STROKE} d="M8 1.8v2M8 12.2v2M1.8 8h2M12.2 8h2" />
      <path {...STROKE} d="m3.8 3.8 1.4 1.4M10.8 10.8l1.4 1.4M12.2 3.8l-1.4 1.4M5.2 10.8l-1.4 1.4" />
    </svg>
  );
}

/** An asset: a picture the scenes name. */
export function AssetsIcon() {
  return (
    <svg {...BOX}>
      <rect {...STROKE} x="1.8" y="3" width="12.4" height="10" rx="1.4" />
      <circle {...STROKE} cx="5.6" cy="6.4" r="1.1" />
      <path {...STROKE} d="m2.6 11.6 3.4-3.2 2.6 2.4 2.2-1.8 2.6 2.6" />
    </svg>
  );
}

/** What has happened here: a clock turned back. */
export function HistoryIcon() {
  return (
    <svg {...BOX}>
      <path {...STROKE} d="M2.6 6.4A5.8 5.8 0 1 1 2.2 9" />
      <path {...STROKE} d="M1.8 3v3.4h3.4" />
      <path {...STROKE} d="M8 5v3.2l2.2 1.4" />
    </svg>
  );
}

/** Collapse the tray body away, or pop it back out. */
export function TrayIcon({ open }: { readonly open: boolean }) {
  return (
    <svg {...BOX}>
      <rect {...STROKE} x="1.8" y="2.6" width="12.4" height="10.8" rx="1.6" />
      <path {...STROKE} d={open ? "M10.4 2.6v10.8" : "M5.6 2.6v10.8"} />
    </svg>
  );
}

/** All the way to the front of the stack. */
export function ToTopIcon() {
  return (
    <svg {...BOX}>
      <path {...STROKE} d="M2.6 2.6h10.8" />
      <path {...STROKE} d="M8 13.4V5.4M4.6 8.8 8 5.4l3.4 3.4" />
    </svg>
  );
}

/** All the way to the back of the stack. */
export function ToBottomIcon() {
  return (
    <svg {...BOX}>
      <path {...STROKE} d="M2.6 13.4h10.8" />
      <path {...STROKE} d="M8 2.6v8M4.6 7.2 8 10.6l3.4-3.4" />
    </svg>
  );
}
